import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiClipboard,
  FiDollarSign,
  FiEye,
  FiFileText,
  FiFilter,
  FiLoader,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function SalesReturnsList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [returns, setReturns] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReturns = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/sales-returns", {
        accessToken,
      });

      setReturns(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(requestError.message || "Could not load sales returns.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadReturns();
  }, [loadReturns]);

  const normalizedReturns = useMemo(
    () =>
      returns.map((salesReturn) => {
        const items = Array.isArray(salesReturn.items)
          ? salesReturn.items
          : [];

        const creditAmount =
          salesReturn.totalCreditAmount !== undefined &&
          salesReturn.totalCreditAmount !== null
            ? Number(salesReturn.totalCreditAmount)
            : items.reduce(
                (total, item) =>
                  total +
                  Number(
                    item.creditAmount ??
                      item.lineCreditAmount ??
                      item.totalAmount ??
                      0,
                  ),
                0,
              );

        return {
          ...salesReturn,
          items,
          creditAmount,
          displayStatus: toTitle(salesReturn.status),
          invoiceCode:
            salesReturn.salesInvoice?.invoiceCode ||
            salesReturn.invoice?.invoiceCode ||
            "—",
          customerName:
            salesReturn.salesInvoice?.customerNameSnapshot ||
            salesReturn.invoice?.customerNameSnapshot ||
            salesReturn.customer?.fullName ||
            "Walk-in Customer",
          storeName: salesReturn.store?.name || "—",
          creditNoteCode:
            salesReturn.creditNote?.creditNoteCode ||
            salesReturn.salesCreditNote?.creditNoteCode ||
            null,
        };
      }),
    [returns],
  );

  const filteredReturns = useMemo(() => {
    const query = search.trim().toLowerCase();

    return normalizedReturns.filter((salesReturn) => {
      const matchesSearch =
        !query ||
        salesReturn.returnCode?.toLowerCase().includes(query) ||
        salesReturn.invoiceCode?.toLowerCase().includes(query) ||
        salesReturn.customerName?.toLowerCase().includes(query) ||
        salesReturn.storeName?.toLowerCase().includes(query);

      const matchesStatus =
        status === "All" || salesReturn.status === status;

      return matchesSearch && matchesStatus;
    });
  }, [normalizedReturns, search, status]);

  const stats = useMemo(
    () => ({
      total: normalizedReturns.length,
      draft: normalizedReturns.filter((item) => item.status === "DRAFT")
        .length,
      received: normalizedReturns.filter((item) => item.status === "RECEIVED")
        .length,
      creditedValue: normalizedReturns
        .filter((item) => item.status === "RECEIVED")
        .reduce((total, item) => total + item.creditAmount, 0),
    }),
    [normalizedReturns],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Sales Returns</h1>

          <p className="mt-2 text-gray-500">
            Receive returned items, restore stock, and issue credit notes.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadReturns}
            disabled={isLoading}
            className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 hover:bg-gray-50 disabled:opacity-60"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>

          <button
            onClick={() => navigate("/admindashboard/sales/returns/create")}
            className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 hover:bg-primary-800"
          >
            <FiPlus />
            Create Sales Return
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Returns"
          value={stats.total}
          icon={<FiRotateCcw size={24} />}
          color="bg-primary-50 text-primary-700"
        />

        <StatCard
          title="Draft Returns"
          value={stats.draft}
          icon={<FiClipboard size={24} />}
          color="bg-yellow-100 text-yellow-700"
        />

        <StatCard
          title="Received"
          value={stats.received}
          icon={<FiCheckCircle size={24} />}
          color="bg-green-100 text-green-700"
        />

        <StatCard
          title="Credit Notes Issued"
          value={formatMoney(stats.creditedValue)}
          icon={<FiDollarSign size={24} />}
          color="bg-blue-100 text-blue-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="grid lg:grid-cols-4 gap-4">
          <div className="relative lg:col-span-3">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search return, invoice, customer, or store..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 focus:outline-none focus:border-primary-700"
            />
          </div>

          <div className="flex gap-3">
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-full h-12 rounded-xl border border-gray-200 px-4 bg-white focus:outline-none focus:border-primary-700"
            >
              <option value="All">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="RECEIVED">Received</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            <button
              onClick={() => {
                setSearch("");
                setStatus("All");
              }}
              className="h-12 w-12 shrink-0 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              title="Reset filters"
            >
              <FiFilter />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-2xl font-bold">Return Register</h2>

          <p className="text-gray-500 mt-1">
            Showing {filteredReturns.length} of {normalizedReturns.length}{" "}
            returns
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1150px]">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Return No.</TableHeader>
                <TableHeader>Invoice</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Store</TableHeader>
                <TableHeader center>Items</TableHeader>
                <TableHeader right>Credit Value</TableHeader>
                <TableHeader>Credit Note</TableHeader>
                <TableHeader center>Status</TableHeader>
                <TableHeader center>Actions</TableHeader>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="9" className="py-20 text-center text-gray-500">
                    <FiLoader className="inline animate-spin mr-2" />
                    Loading sales returns...
                  </td>
                </tr>
              ) : filteredReturns.length ? (
                filteredReturns.map((salesReturn) => (
                  <tr
                    key={salesReturn.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition"
                  >
                    <td className="px-5 py-5">
                      <h3 className="font-semibold text-primary-700">
                        {salesReturn.returnCode}
                      </h3>

                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(salesReturn.createdAt)}
                      </p>
                    </td>

                    <td className="px-5 py-5">
                      <span className="font-medium">
                        {salesReturn.invoiceCode}
                      </span>
                    </td>

                    <td className="px-5 py-5">
                      <h3 className="font-medium">{salesReturn.customerName}</h3>

                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                        {salesReturn.reason || "No reason recorded"}
                      </p>
                    </td>

                    <td className="px-5 py-5">{salesReturn.storeName}</td>

                    <td className="px-5 py-5 text-center">
                      {salesReturn.items.length}
                    </td>

                    <td className="px-5 py-5 text-right font-semibold">
                      {formatMoney(salesReturn.creditAmount)}
                    </td>

                    <td className="px-5 py-5">
                      {salesReturn.creditNoteCode ? (
                        <span className="inline-flex items-center gap-2 text-green-700 font-medium">
                          <FiFileText />
                          {salesReturn.creditNoteCode}
                        </span>
                      ) : (
                        <span className="text-gray-400">Pending receipt</span>
                      )}
                    </td>

                    <td className="px-5 py-5 text-center">
                      <ReturnStatusBadge status={salesReturn.displayStatus} />
                    </td>

                    <td className="px-5 py-5">
                      <div className="flex justify-center">
                        <button
                          title="View sales return"
                          onClick={() =>
                            navigate(
                              `/admindashboard/sales/returns/${salesReturn.id}`,
                            )
                          }
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="py-20 text-center">
                    <FiRotateCcw
                      size={34}
                      className="mx-auto text-gray-300 mb-3"
                    />
                    <p className="font-medium text-gray-700">
                      No sales returns found
                    </p>
                    <button
                      onClick={() =>
                        navigate("/admindashboard/sales/returns/create")
                      }
                      className="mt-4 inline-flex items-center gap-2 text-primary-700 font-semibold"
                    >
                      Create the first sales return
                      <FiArrowRight />
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h2 className="text-2xl font-bold mt-3">{value}</h2>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function ReturnStatusBadge({ status }) {
  const colors = {
    Draft: "bg-yellow-100 text-yellow-700",
    Received: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}